/**
 * TransferLinesTable Component
 *
 * Read-only table displaying transfer lines with packaging info.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see Sprint 22 - Transferts par Packaging
 */

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui';
import type { TransferLineWithProduct } from '@bstock/shared';
import { formatCentimes } from '@/lib/format';

interface TransferLinesTableProps {
  lines: TransferLineWithProduct[];
  currency: string;
}

/**
 * Helper to format a quantity with optional packaging sub-text
 */
function formatQuantityWithPackaging(
  qty: number | null,
  unit: string,
  packagingName: string | null,
  packagingQty: number | null,
): JSX.Element | string {
  if (qty === null) return '-';
  if (packagingName && packagingQty != null) {
    return (
      <div>
        <div>{qty} {unit}</div>
        <div className="text-xs text-neutral-400">{packagingQty} x {packagingName}</div>
      </div>
    );
  }
  return `${qty} ${unit}`;
}

export function TransferLinesTable({ lines, currency }: TransferLinesTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Produit</TableHead>
          <TableHead className="text-right">Demande</TableHead>
          <TableHead className="text-right">Expedie</TableHead>
          <TableHead className="text-right">Recu</TableHead>
          <TableHead className="text-right">Valeur</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((line) => {
          const unit = line.rawProduct.recipeUnit.symbol;
          return (
            <TableRow key={line.id}>
              <TableCell>
                <div className="font-medium">{line.rawProduct.name}</div>
                <div className="text-xs text-neutral-500">
                  {line.rawProduct.sku} | {unit}
                </div>
              </TableCell>
              <TableCell className="text-right">
                {formatQuantityWithPackaging(
                  line.quantityRequested,
                  unit,
                  line.requestPackagingName,
                  line.requestPackagingQty,
                )}
              </TableCell>
              <TableCell className="text-right">
                {formatQuantityWithPackaging(
                  line.quantityShipped,
                  unit,
                  line.shippedPackagingName,
                  line.shippedPackagingQty,
                )}
              </TableCell>
              <TableCell className="text-right">
                {line.quantityReceived !== null ? (
                  <div>
                    <span className={line.variance && line.variance !== 0 ? 'text-warning-600' : ''}>
                      {line.quantityReceived} {unit}
                      {line.variance !== null && line.variance !== 0 && (
                        <span className="text-xs ml-1">({line.variance > 0 ? '+' : ''}{line.variance})</span>
                      )}
                    </span>
                    {line.receivedPackagingName && line.receivedPackagingQty != null && (
                      <div className="text-xs text-neutral-400">
                        {line.receivedPackagingQty} x {line.receivedPackagingName}
                      </div>
                    )}
                  </div>
                ) : '-'}
              </TableCell>
              <TableCell className="text-right">
                {line.totalCostCentimes > 0 ? formatCentimes(line.totalCostCentimes, currency) : '-'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
